// Draw priority of loaded point cloud blocks for one camera.
//
// The server already limits what is downloaded; this orders what is in memory so that a frame can draw
// only the blocks that matter most and skip the rest without disposing them. Blocks are ranked by the
// screen spacing of their own points, sparsest first: that puts coarse octree nodes (which cover whole
// regions) before their refinements and near blocks before far ones, the same order the point budget LOD
// on the server uses, so cutting the ranked list at a budget keeps every drawn block's ancestors drawn.

/** The world-space bounds and point count of one loaded block. */
export interface HsPointCloudBlockView {
  boundingBox: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } };
  pointCount: number;
}

/** The camera as the ranking needs it. */
export interface HsPointCloudDrawView {
  /** World-space camera position. */
  cameraPosition: ArrayLike<number>;
  /** Screen pixels per world unit one unit in front of the camera: viewport height * projection[5] / 2. */
  pixelsPerUnitAtUnitDistance: number;
}

export interface HsPointCloudDrawOrderEntry {
  /** Index into the blocks array the order was built from. */
  index: number;
  /** Projected spacing of the block's points in pixels. */
  spacing: number;
}

/**
 * PotreeConverter 2 samples each octree node on a 128^3 grid, so a node's cube edge spans 128 point
 * spacings. The viewer sees the tight bounds of a node's points, at most the cube, so the estimate errs
 * towards a smaller spacing (a denser block), which only ranks it later.
 */
export const HS_POINT_CLOUD_NODE_EDGE_TO_SPACING: number = 128;

/** Keeps blocks that contain the camera finite and first. */
const MIN_DISTANCE: number = 1e-3;

/** Projected spacing of the block's points in pixels, from its largest extent and its distance to the camera. */
export function hsProjectedPointSpacing(block: HsPointCloudBlockView, view: HsPointCloudDrawView): number {
  const { min, max } = block.boundingBox;
  const edge: number = Math.max(max.x - min.x, max.y - min.y, max.z - min.z);
  const cx: number = view.cameraPosition[0];
  const cy: number = view.cameraPosition[1];
  const cz: number = view.cameraPosition[2];
  const dx: number = Math.max(min.x - cx, 0, cx - max.x);
  const dy: number = Math.max(min.y - cy, 0, cy - max.y);
  const dz: number = Math.max(min.z - cz, 0, cz - max.z);
  const distance: number = Math.max(Math.sqrt(dx * dx + dy * dy + dz * dz), MIN_DISTANCE);
  return (edge / HS_POINT_CLOUD_NODE_EDGE_TO_SPACING) * (view.pixelsPerUnitAtUnitDistance / distance);
}

/** Blocks ranked sparsest on screen first; ties keep the input order. */
export function hsOrderPointCloudBlocks(
  blocks: readonly HsPointCloudBlockView[],
  view: HsPointCloudDrawView
): HsPointCloudDrawOrderEntry[] {
  const order: HsPointCloudDrawOrderEntry[] = new Array<HsPointCloudDrawOrderEntry>(blocks.length);
  for (let index: number = 0; index < blocks.length; index++) {
    order[index] = { index, spacing: hsProjectedPointSpacing(blocks[index], view) };
  }
  order.sort((a: HsPointCloudDrawOrderEntry, b: HsPointCloudDrawOrderEntry) =>
    b.spacing !== a.spacing ? b.spacing - a.spacing : a.index - b.index
  );
  return order;
}

export interface HsPointCloudDrawSelection {
  /** One entry per block: 1 when the block is drawn. */
  drawn: Uint8Array;
  /** One entry per block: how many of its first points are drawn, 0 when it is skipped. */
  counts: Uint32Array;
  drawnPoints: number;
  drawnBlocks: number;
  /** Points every block would draw without a budget: the ceiling the budget can grow towards. */
  wantedPoints: number;
}

/**
 * Points of a block worth drawing at `spacing` pixels between its points: all of them while the spacing is
 * at least `targetSpacingPixels`, else the share that thins the block to the target spacing. Screen
 * density cannot exceed one point per pixel, so a block projecting at a third of the target is drawn at
 * a ninth of its points. Decoders interleave blocks, so the first `n` points are an even sample.
 */
export function hsPointCloudDrawCount(pointCount: number, spacing: number, targetSpacingPixels: number): number {
  if (pointCount <= 0) {
    return 0;
  }
  if (!(targetSpacingPixels > 0) || !(spacing < targetSpacingPixels)) {
    return pointCount;
  }
  const share: number = (Math.max(0, spacing) / targetSpacingPixels) ** 2;
  return Math.min(pointCount, Math.max(1, Math.round(pointCount * share)));
}

/**
 * Draws ranked blocks in order until the next one would exceed `budgetPoints`, then stops: later blocks
 * are finer or farther, and stopping (rather than skipping ahead) keeps the drawn set closed under
 * ancestors. The first block is always drawn. With `targetSpacingPixels`, each block draws only
 * `hsPointCloudDrawCount` points and the budget counts those, so dense near blocks fit where whole ones
 * would not; without it every drawn block is drawn whole.
 */
export function hsSelectPointCloudBlocks(
  order: readonly HsPointCloudDrawOrderEntry[],
  blocks: readonly HsPointCloudBlockView[],
  budgetPoints: number,
  targetSpacingPixels: number = 0
): HsPointCloudDrawSelection {
  const drawn: Uint8Array = new Uint8Array(blocks.length);
  const counts: Uint32Array = new Uint32Array(blocks.length);
  let drawnPoints: number = 0;
  let drawnBlocks: number = 0;
  let wantedPoints: number = 0;
  let stopped: boolean = false;
  for (let position: number = 0; position < order.length; position++) {
    const { index, spacing } = order[position];
    const points: number = hsPointCloudDrawCount(blocks[index].pointCount, spacing, targetSpacingPixels);
    wantedPoints += points;
    if (stopped || (drawnBlocks > 0 && drawnPoints + points > budgetPoints)) {
      stopped = true;
      continue;
    }
    drawn[index] = 1;
    counts[index] = points;
    drawnPoints += points;
    drawnBlocks++;
  }
  return { drawn, counts, drawnPoints, drawnBlocks, wantedPoints };
}

/** Point size per projected spacing for adaptive sizes: Poisson-disk mean spacing is above the minimum spacing. */
export const HS_POINT_CLOUD_ADAPTIVE_SIZE_SPACING_FACTOR: number = 1.3;
/** Adaptive point sizes stay within this multiple of the base point size. */
export const HS_POINT_CLOUD_ADAPTIVE_SIZE_MAX_FACTOR: number = 2.5;
/** A descendant is at most this share of its ancestor's largest extent (octree children have half of it). */
const DESCENDANT_MAX_EDGE_SHARE: number = 0.75;
/** Tight boxes of a node's points can stick out of its parent's tight box by a little. */
const DESCENDANT_BOX_MARGIN_SHARE: number = 0.02;

/**
 * The loaded descendants of every block: the smaller blocks whose center lies in its box. Loaded octree
 * levels overlap, so a block's region is also covered by the points of its descendants. Blocks are sorted
 * along x once, so each block only scans the blocks within its x range.
 */
export function hsPointCloudBlockDescendants(blocks: readonly HsPointCloudBlockView[]): Uint32Array[] {
  const count: number = blocks.length;
  const centerX: Float64Array = new Float64Array(count);
  const edges: Float64Array = new Float64Array(count);
  const byX: number[] = new Array<number>(count);
  for (let index: number = 0; index < count; index++) {
    const { min, max } = blocks[index].boundingBox;
    centerX[index] = (min.x + max.x) / 2;
    edges[index] = Math.max(max.x - min.x, max.y - min.y, max.z - min.z);
    byX[index] = index;
  }
  byX.sort((a: number, b: number) => centerX[a] - centerX[b]);
  const sortedX: Float64Array = Float64Array.from(byX, (index: number) => centerX[index]);
  const descendants: Uint32Array[] = new Array<Uint32Array>(count);
  const found: number[] = [];
  for (let index: number = 0; index < count; index++) {
    const { min, max } = blocks[index].boundingBox;
    const margin: number = edges[index] * DESCENDANT_BOX_MARGIN_SHARE;
    found.length = 0;
    for (let position: number = lowerBound(sortedX, min.x - margin); position < count; position++) {
      if (sortedX[position] > max.x + margin) {
        break;
      }
      const other: number = byX[position];
      if (other === index || edges[other] > edges[index] * DESCENDANT_MAX_EDGE_SHARE) {
        continue;
      }
      const box: HsPointCloudBlockView['boundingBox'] = blocks[other].boundingBox;
      const y: number = (box.min.y + box.max.y) / 2;
      const z: number = (box.min.z + box.max.z) / 2;
      if (y >= min.y - margin && y <= max.y + margin && z >= min.z - margin && z <= max.z + margin) {
        found.push(other);
      }
    }
    descendants[index] = Uint32Array.from(found);
  }
  return descendants;
}

/**
 * Adaptive point size of a block (after Schütz, "Rendering Large Point Clouds in Web Browsers", 2015):
 * `HS_POINT_CLOUD_ADAPTIVE_SIZE_SPACING_FACTOR` times the spacing of the points drawn over the block's
 * region, within the base size and `HS_POINT_CLOUD_ADAPTIVE_SIZE_MAX_FACTOR` times it. The block's own
 * `spacing` (all `pointCount` points) widens by the square root of how many fewer points cover the region:
 * `coveringPoints` counts the block's drawn points plus those of its drawn descendants.
 */
export function hsAdaptivePointSize(
  spacing: number,
  pointCount: number,
  coveringPoints: number,
  basePointSize: number
): number {
  const drawnSpacing: number = coveringPoints > 0 ? spacing * Math.sqrt(pointCount / coveringPoints) : spacing;
  const size: number = HS_POINT_CLOUD_ADAPTIVE_SIZE_SPACING_FACTOR * drawnSpacing;
  return Math.min(basePointSize * HS_POINT_CLOUD_ADAPTIVE_SIZE_MAX_FACTOR, Math.max(basePointSize, size));
}

function lowerBound(sorted: Float64Array, value: number): number {
  let low: number = 0;
  let high: number = sorted.length;
  while (low < high) {
    const middle: number = (low + high) >>> 1;
    if (sorted[middle] < value) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  return low;
}
